import { ReactNode, useCallback, useMemo, useState } from 'react'
import moment from 'moment-timezone'
import compose from 'recompose/compose'
import { isEmpty } from 'ramda'
import { useSelector } from 'react-redux'

import { withKitsKitsRights, withCurrentOrgRights /* , omitExtra */ } from 'app/containers/rights'
import { withWriteAccess } from 'app/common/containers/withRights'
import PlainButton from 'app/common/ui-next/plain-button'
import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link'
import PlainStatusFilter from 'app/common/ui-next/plain-status-filter'
import ControlsBar, { ControlsBarSpace } from 'app/common/ui-next/controls-bar'
import { PageTemplateContent, Header2 } from 'app/common/templates-next/page-template'
import Sticky from 'app/common/templates-next/sticky'
import { useGeneralSettings } from 'app/dataProviders/generalSettings/hooks'
import SelectorDietFoodTypes from './SelectorDietFoodTypes'

import KitsListSimple from './KitsList/KitsListSimple'
import KitsListExt from './KitsList/KitsListExt'
import NewEditKit from './NewEditKit'
import DateNavigation from '../DateNavigation'
import { status2TranslationMap, status2ColorMap, Status, isStatus } from './statusProps'
import { CopyKitModal } from './CopyKit'
import useKits from './useKits'
import useKitsIDs from './useKitsIDs'
import useKitsTestFixedPriceFlag from './useKitsTestFixedPriceFlag'
import { HeaderOrgsKits } from './HeaderOrgsKits'

import styles from './OrgsKits.scss'
import PlainIcon, { colorTypes, iconTypes } from 'app/common/ui-next/plain-icon'
import { SortKitModal } from './SortKitModal'
import { OrgId } from 'app/common/types/id'
import BuildingsData, { BuildingsView, type Building } from 'app/components/common/BuildingsData'
import { RowLayoutContent } from 'app/common/layouts/RowLayout'
import { HeaderLvl2 } from 'app/common/layouts/markup'
import { useSchoolDietFoodTypes } from 'app/dataProviders/org/dietFoodTypes/hooks'
import { useDietFoodTypePath } from './useDietFoodTypePath'
import { CopyComplexesLoader } from '../commonComponents/copyComplexesLoader'

const writeAccess = compose(
  withCurrentOrgRights,
  withWriteAccess,
  withKitsKitsRights,
  withWriteAccess,
)
// const AccessedButton = writeAccess(omitExtra(Button));
const AccessedNewEditKit = writeAccess(NewEditKit)

type Props = {
  orgID: string
  orgType: string
  kitID?: string
  date: string
  dateFormat: string
  filter?: string
  redirectToDate: (day: string, format: string) => void
  redirectToFilter: (filter: string) => void
  basePath: string
  schoolName?: string
}

const OrgsKits = ({
  orgID,
  orgType,
  kitID,
  date,
  dateFormat,
  filter,
  redirectToDate,
  redirectToFilter,
  basePath,
  schoolName,
}: Props): ReactNode => {
  const mDate = moment(date)
  const { data: dietFoodTypes, loading: dietFoodTypesLoading } = useSchoolDietFoodTypes({
    params: { orgId: orgID as OrgId },
  })

  const kits = useKits()
  const fixedPriceEnabled = useKitsTestFixedPriceFlag(kits)
  const copyInProgress = useSelector<any, boolean>((state) =>
    Boolean(state.orgs.kits.options.copyInProgress),
  )
  const { generalSettings } = useGeneralSettings()
  const kitIDs = useKitsIDs(kits)
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null)
  const { dietFoodTypeId, onSelectDietFoodType } = useDietFoodTypePath({
    items: dietFoodTypes,
    loading: dietFoodTypesLoading,
  })

  const [isNeedToRequest, setNeedReauest] = useState<boolean>(false)

  const handleSuccessRequest = () => {
    setNeedReauest(false)
  }

  const handleSortSuccess = () => {
    setNeedReauest(true)
  }

  const handleSelectBuilding = useCallback(
    (building: Building) => {
      if (generalSettings.useComplexBuildingVariants) {
        setSelectedBuilding(building)
      }
    },
    [generalSettings.useComplexBuildingVariants],
  )

  const filterAsStatus = filter && isStatus(filter) ? filter : null

  //Заголовки
  const currentFoodTypeTitle = useMemo(() => {
    if (!dietFoodTypes.length) return ''
    const name = dietFoodTypes.find((item) => item.id === dietFoodTypeId)?.title || 'Базовый'
    return `Набор комплекта: «${name}»`
  }, [dietFoodTypes, dietFoodTypeId])

  return (
    <BuildingsData
      orgId={parseInt(orgID, 10)}
      selectorModalTitle="Выберите корпус ОУ"
      onChangeSelected={handleSelectBuilding}
    >
      <div className={styles.root}>
        <Sticky>
          {(ref) => (
            <PageTemplateContent className={styles.hasPopup} reference={ref} horizontalLayout>
              <RowLayoutContent className={styles.contentShift}>
                <Header2 skipMargin wide>
                  <HeaderOrgsKits action={filterAsStatus} mDate={mDate} />
                </Header2>
                {generalSettings.useComplexBuildingVariants && (
                  <BuildingsView title="Корпус ОУ:" className={styles.buildingsView} />
                )}
                <DateNavigation date={date} dateFormat={dateFormat} onDateChange={redirectToDate} />
              </RowLayoutContent>
            </PageTemplateContent>
          )}
        </Sticky>

        <Sticky>
          {(ref, _, shadow) => (
            <PageTemplateContent reference={ref} shadowBottom={shadow}>
              {!!dietFoodTypes.length && (
                <RowLayoutContent className={styles.contentShift} borderBottom>
                  <div className={styles.container}>
                    <ControlsBar className={styles.navContainer} top>
                      <SelectorDietFoodTypes
                        items={dietFoodTypes}
                        title="Набор комплектов"
                        className={styles.nav}
                        loading={dietFoodTypesLoading}
                        scrollable
                        selectedId={dietFoodTypeId}
                        onSelect={onSelectDietFoodType}
                      />
                    </ControlsBar>
                    <HeaderLvl2>{currentFoodTypeTitle}</HeaderLvl2>
                  </div>
                </RowLayoutContent>
              )}
              <ControlsBar>
                {/* Добавление нового комплекта или редактирование */}
                {filter !== Status.Archived && (
                  <AccessedNewEditKit orgId={parseInt(orgID, 10)} dietFoodTypeId={dietFoodTypeId}>
                    {(toggleModal) => (
                      <PlainButton onClick={toggleModal}>{'Создать комплект'}</PlainButton>
                    )}
                  </AccessedNewEditKit>
                )}

                {/* Копировать все комплекты */}
                {filter !== Status.Archived && !isEmpty(kitIDs) && (
                  <CopyKitModal
                    orgID={orgID}
                    buildingId={selectedBuilding?.id}
                    kitID={kitIDs}
                    header="Копирование всех комплектов"
                    date={date}
                    fixedPriceEnabled={fixedPriceEnabled}
                  >
                    {(toggle) => (
                      <PlainButtonAsLink onClick={toggle}>
                        {'Копировать все комплекты'}
                      </PlainButtonAsLink>
                    )}
                  </CopyKitModal>
                )}

                <SortKitModal
                  orgId={orgID as OrgId}
                  kits={kits}
                  header="Упорядочить комплекты"
                  onSuccessSubmit={handleSortSuccess}
                >
                  {(toggle) => (
                    <PlainButtonAsLink
                      onClick={toggle}
                      linkClassName={styles.sortButton}
                      disabled={false}
                    >
                      <PlainIcon
                        width="25px"
                        height="auto"
                        type={iconTypes.SORT}
                        colorType={colorTypes.COLOR_PRIMARY}
                      />
                    </PlainButtonAsLink>
                  )}
                </SortKitModal>
                <ControlsBarSpace />

                <PlainStatusFilter
                  currentStatus={filter ?? ''}
                  onClickItem={redirectToFilter}
                  status2ColorMap={status2ColorMap}
                  status2TranslationMap={status2TranslationMap}
                  title="Показать"
                />
              </ControlsBar>
            </PageTemplateContent>
          )}
        </Sticky>

        {copyInProgress && <CopyComplexesLoader />}

        <PageTemplateContent>
          {filter && filter === Status.Archived && <KitsListSimple orgID={orgID} />}
          {filter !== Status.Archived && (
            <KitsListExt
              action={filter}
              orgID={orgID}
              orgType={orgType}
              useGrocerySetPrice={generalSettings.useGrocerySetPrice}
              kitID={kitID}
              buildingId={selectedBuilding?.id}
              dietFoodTypeId={dietFoodTypeId}
              dietFoodTypeTitle={currentFoodTypeTitle}
              useComplexBuildingVariants={generalSettings.useComplexBuildingVariants}
              date={date}
              basePath={basePath}
              isNeedToRequest={isNeedToRequest}
              onSuccessRequest={handleSuccessRequest}
              schoolName={schoolName}
            />
          )}
        </PageTemplateContent>
      </div>
    </BuildingsData>
  )
}

export default OrgsKits
